import React, { useState, useMemo } from 'react';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { CategorizeTaxIdPicker } from '../../../categorizeTransacrions/components/CategorizeTaxIdPicker';
import { ICategory, ITransaction } from '../../../../types';
import { SplitTransaction } from '../../../categorizeTransacrions/components/SplitTransaction';
import { ACTION_TYPES } from '../../../categorizeTransacrions/constants';
import { SplitLink } from '../../../categorizeTransacrions/components/SplitTransaction/SplitLink';
import {
  getCategoryIdFromTransaction,
  isCategorizationTypeIncome,
  isCategorized,
  showUserSplitOption,
} from '../../../transactions/utils/categorization';
import { ReactComponent as CloseIcon } from '../../../../assets/svg/invoices-icons/icon-close-icon.svg';
import { ReactComponent as BackIcon } from '../../../../assets/svg/backArrow.svg';
import { BUSINESS_PERCENT } from '../../../expenses/constans';
import { useStore } from '../../../../store';

const POPUP_STYLES = {
  main: 'bg-bw07 flex fixed inset-0 bg-opacity-70 z-[50] justify-center items-center',
  content: 'cursor-default block w-[360px] h-[560px] rounded-[10px] static bg-bw00',
  exit: 'absolute top-4 left-4 cursor-pointer z-10',
};

interface IProps {
  categoryList: ICategory[];
  handleCategoryPick: (catId: string | null, businessPercent?: number) => void;
  transaction: ITransaction;
  categorizationType?: string;
  allowCategorizationEnum?: string;
  actionType: string;
}

export const STEPS = {
  CHOOSE_CATEGORY: 1,
  SPLIT: 2,
  CHOOSE_CATEGORY_NO_SPLIT: 3,
};

const getCategorizationPopupStep = (actionType: string) => {
  if ([ACTION_TYPES.APPROVE, ACTION_TYPES.NOT_NOW, ''].includes(actionType)) return STEPS.CHOOSE_CATEGORY_NO_SPLIT;
  if (actionType === ACTION_TYPES.SPLIT) return STEPS.SPLIT;
  return STEPS.CHOOSE_CATEGORY;
};

export const CategorizationPopup = ({ categoryList, handleCategoryPick, transaction, actionType }: IProps) => {
  const [currentStep, setCurrentStep] = useState<number>(getCategorizationPopupStep(actionType || ''));
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(getCategoryIdFromTransaction(transaction));
  const [lastActionType, setLastActionType] = useState<string>('');
  const [selectedBusinessPercent, setSelectedBusinessPercent] = useState<number | null>(null);
  const setSplitTransactionBusinessPercent = useStore((state) => state.setSplitTransactionBusinessPercent);
  const filteredCategoriesList = useMemo(
    () => categoryList.filter((category) => category.transactionType === transaction.categorizationType),
    [transaction.categorizationType],
  );
  const handleShowSplitClick = () => {
    setLastActionType(ACTION_TYPES.SPLIT);
    setCurrentStep(STEPS.SPLIT);
  };
  const showCategoriesList = () =>
    currentStep === STEPS.CHOOSE_CATEGORY || currentStep === STEPS.CHOOSE_CATEGORY_NO_SPLIT;
  const showSplitTransaction = () => currentStep === STEPS.SPLIT;
  const showSplitLink =
    showUserSplitOption(transaction?.allowCategorizationEnum || '', transaction?.categorizationType || '') &&
    currentStep === STEPS.CHOOSE_CATEGORY;
  const isCategorizedTransaction = isCategorized(transaction?.categoryDetails);

  const onSplitTransactionClick = (catId: string | null, businessPercent: number | null) => {
    if (catId === null) setCurrentStep(STEPS.CHOOSE_CATEGORY_NO_SPLIT);
    else if (catId !== null && businessPercent !== null) {
      setSelectedBusinessPercent(businessPercent);
      handleCategoryPick(catId, businessPercent);
    }
  };

  const handleBackToChooseCategory = () => {
    setLastActionType('');
    setCurrentStep(STEPS.CHOOSE_CATEGORY);
  };

  const handleSplitClose = () => {
    setSplitTransactionBusinessPercent(null);
    if (lastActionType === ACTION_TYPES.SPLIT) {
      handleBackToChooseCategory();
    } else {
      handleCategoryPick(null);
    }
  };

  const handleChooseCategoryBack = () => {
    setCurrentStep(STEPS.SPLIT);
  };

  const handleCategoryPickSelection = (catId: string | null, businessPercent?: number) => {
    if (catId === null) {
      handleCategoryPick(catId);
    } else if (currentStep === STEPS.CHOOSE_CATEGORY_NO_SPLIT) {
      if (
        selectedBusinessPercent === null &&
        (actionType === ACTION_TYPES.SPLIT || lastActionType === ACTION_TYPES.SPLIT)
      ) {
        setSelectedCategoryId(catId);
        setCurrentStep(STEPS.SPLIT);
      } else if (selectedBusinessPercent === null && actionType === ACTION_TYPES.APPROVE) {
        handleCategoryPick(catId);
      }
    } else if (currentStep === STEPS.CHOOSE_CATEGORY) {
      if (
        typeof businessPercent === 'number' &&
        [BUSINESS_PERCENT.WORK, BUSINESS_PERCENT.LIFE].includes(businessPercent)
      ) {
        handleCategoryPick(catId, businessPercent);
      } else if ([ACTION_TYPES.CHOOSE_CATEGORY, ACTION_TYPES.WORK].includes(actionType) && lastActionType === '') {
        handleCategoryPick(catId);
      }
    }
  };
  const shouldShowCategorizationPanel =
    isCategorizedTransaction &&
    !!transaction?.categorizationType &&
    !isCategorizationTypeIncome(transaction.categorizationType) &&
    currentStep !== STEPS.CHOOSE_CATEGORY_NO_SPLIT;
  return (
    <div>
      <UserMessage
        id='invoices-modal'
        mainWrapperOverrideClassName={POPUP_STYLES.main}
        contentWrapperOverrideClassName={POPUP_STYLES.content}
        onClick={() => ({})}
      >
        {showCategoriesList() && (
          <CategorizeTaxIdPicker
            activeCategoryId={selectedCategoryId}
            handlePickCategoryId={handleCategoryPickSelection}
            categoryList={filteredCategoriesList}
            className='border border-bw03 shadow-light'
            headerLeftAddon={
              currentStep === STEPS.CHOOSE_CATEGORY_NO_SPLIT &&
              !isCategorizationTypeIncome(transaction.categorizationType || '') ? (
                <BackIcon className='w-[8px] cursor-pointer relative top-[-1px]' onClick={handleChooseCategoryBack} />
                ) : (
                  <CloseIcon
                    className='rounded-full fill-bw07 hover:bg-bw02 cursor-pointer relative top-[-1px]'
                    onClick={() => handleCategoryPick(null)}
                  />
                )
            }
            headerRightAddon={showSplitLink && <SplitLink handleOnClick={handleShowSplitClick} />}
            showAnimation={false}
            categorizationType={transaction?.categorizationType}
            showCategorizationPanel={shouldShowCategorizationPanel}
          />
        )}
        {showSplitTransaction() && (
          <SplitTransaction
            handleSubmit={onSplitTransactionClick}
            selectedCategoryId={selectedCategoryId}
            transaction={transaction}
            onChooseCategoryClick={() => setCurrentStep(STEPS.CHOOSE_CATEGORY_NO_SPLIT)}
            headerIcon={
              lastActionType === ACTION_TYPES.SPLIT ? (
                <BackIcon className='w-[8px] cursor-pointer relative top-[-1px]' onClick={handleSplitClose} />
              ) : (
                <CloseIcon
                  className='rounded-full fill-bw07 hover:bg-bw02 cursor-pointer relative top-[-1px]'
                  onClick={handleSplitClose}
                />
              )
            }
          />
        )}
      </UserMessage>
    </div>
  );
};
